import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IconGrid, IconStore, IconUser } from './icons';
import { SignInPage } from '../pages/SignInPage';
import { CodeBoxMark } from './BrandLogo';
import { ThemeToggle } from './ThemeToggle';
import { TokenBalance } from './studio/TokenMeter';

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  [
    'flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition',
    isActive ? 'bg-accent/20 text-ink' : 'text-muted hover:bg-page hover:text-ink',
  ].join(' ');

/**
 * App chrome. The studio route hides it — the old Code Box goes full-bleed the moment you are
 * building, because the preview is the product and every pixel of chrome competes with it.
 */
export function AppShell() {
  const { status, user } = useAuth();
  const { pathname } = useLocation();
  const isStudio = /^\/projects\/[^/]+$/.test(pathname);

  if (status === 'loading') {
    return (
      <div className="grid h-full place-items-center">
        <div className="space-y-3 text-center">
          <div className="skeleton mx-auto h-9 w-9 rounded-2xl" />
          <p className="text-sm text-muted">Loading your workspace</p>
        </div>
      </div>
    );
  }

  if (status === 'anonymous') return <SignInPage />;

  if (isStudio) {
    return (
      <div className="h-full">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-line bg-surface/80 px-4 backdrop-blur md:px-8">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2.5">
            <CodeBoxMark className="h-8 w-auto" />
            <div className="leading-tight">
              <p className="text-base font-extrabold">Code Box</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Studio</p>
            </div>
          </div>
          <nav className="flex items-center gap-1">
            <NavLink to="/projects" className={navItemClass}>
              <IconGrid />
              <span className="hidden sm:inline">My creations</span>
            </NavLink>
            <NavLink to="/playstore" className={navItemClass}>
              <IconStore />
              <span className="hidden sm:inline">Playstore</span>
            </NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted">
          <ThemeToggle />
          <IconUser />
          <span className="hidden max-w-[18ch] truncate sm:inline">
            {user?.displayName ?? user?.email}
          </span>
          <TokenBalance />
          {user?.plan === 'pro' && (
            <span className="rounded-full bg-sun/25 px-2 py-0.5 text-xs font-bold text-accent-deep">Pro</span>
          )}
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
